
/**
 * Utility for making authenticated API requests
 */

import { supabase } from "@/integrations/supabase/client";

// Function to get the auth token from localStorage
export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

// Webhook credentials cache to avoid repeated API calls
let credentialsCache: { username: string; password: string } | null = null;
let cacheExpiry: number = 0;

// Get webhook credentials securely from Supabase edge function
const getWebhookCredentials = async (): Promise<{ username: string; password: string }> => {
  // Return cached credentials if still valid (cache for 5 minutes)
  if (credentialsCache && Date.now() < cacheExpiry) {
    return credentialsCache;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('User not authenticated');
    }

    const response = await supabase.functions.invoke('get-webhook-credentials', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (response.error) {
      throw new Error(`Failed to get credentials: ${response.error.message}`);
    }

    credentialsCache = response.data;
    cacheExpiry = Date.now() + 300000; // Cache for 5 minutes
    return credentialsCache;
  } catch (error) {
    console.error('Failed to get webhook credentials:', error);
    // Fallback to default credentials as a last resort
    return {
      username: 'Sidekick',
      password: 'default_password'
    };
  }
};

// Different webhook URLs for different interaction types
const WEBHOOK_URLS = {
  file: 'https://n8n.k2innoventions.com/webhook/1587fb4a-79c2-40ee-998c-5b7ef86feb6e',
  chat: 'https://n8n.k2innoventions.com/webhook/d665fbb3-e9a2-4176-ba1d-585beadbbec3',
  voice: 'https://n8n.k2innoventions.com/webhook/d665fbb3-e9a2-4176-ba1d-585beadbbec3'
};

// Function to create Basic Auth token
const createBasicAuthToken = (username: string, password: string): string => {
  return btoa(`${username}:${password}`);
};

// Function to make authenticated API requests
export const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }
  
  // Add Authorization header with Basic auth
  const headers = {
    ...options.headers,
    'Authorization': `Basic ${token}`
  };
  
  return fetch(url, {
    ...options,
    headers,
    credentials: 'include'
  });
};

// Function for sending messages to the chat API
export const sendMessage = async (
  message: string, 
  persona: string, 
  files?: Array<{ name: string; url: string; type: string; file?: File; fileType?: string; fileName?: string }>,
  sessionId?: string,
  userId?: string
): Promise<{ message: string }> => {
  // Use different webhook URLs based on interaction type
  const webhookUrl = files && files.length > 0 ? WEBHOOK_URLS.file : WEBHOOK_URLS.chat;
  
  console.log("===== SEND MESSAGE API =====");
  console.log("Sending message:", message);
  console.log("Persona/Category:", persona);
  console.log("Files:", files);
  console.log("Session ID:", sessionId);
  console.log("User ID:", userId);
  console.log("Using webhook URL:", webhookUrl);
  console.log("Interaction type:", files && files.length > 0 ? 'FILE' : 'CHAT');
  console.log("Current origin:", window.location.origin);
  console.log("User agent:", navigator.userAgent);
  
  const credentials = await getWebhookCredentials();
  const basicAuthToken = createBasicAuthToken(credentials.username, credentials.password);
  
  try {
    let response;
    
    // If files are present, use FormData and set messageType to "file"
    if (files && files.length > 0) {
      console.log("Sending request with files using FormData to file webhook...");
      
      const formData = new FormData();
      formData.append('text', message);
      formData.append('category', persona);
      formData.append('persona', persona);
      formData.append('messageType', 'file'); // Explicitly set to "file" for file uploads
      formData.append('sessionId', sessionId || '');
      formData.append('userId', userId || '');
      // Add basic auth as form data to avoid preflight
      formData.append('auth', basicAuthToken);
      
      // Handle the first file - check if it's a web URL or actual file
      const file = files[0];
      
      if (file.type === 'web' || file.fileType === 'web') {
        // For web URLs, send the URL and set fileType to "web"
        console.log("Adding web URL:", file.url);
        formData.append('webUrl', file.url);
        formData.append('fileName', file.fileName || file.url); // Use fileName field which contains the URL
        formData.append('fileType', 'web');
      } else if (file.file) {
        // For actual files, send as binary
        console.log("Adding file as binary 'file' field:", file.name);
        formData.append('file', file.file, file.name);
        formData.append('fileName', file.fileName || file.name); // Use fileName if available, otherwise use name
        formData.append('fileType', file.type);
      }
      
      console.log("Making FormData POST request with messageType 'file'...");
      
      try {
        response = await fetch(webhookUrl, {
          method: "POST",
          body: formData
        });
        console.log("FormData POST request completed successfully");
      } catch (fetchError) {
        console.error("FormData fetch failed:", fetchError);
        console.error("Error name:", fetchError.name);
        console.error("Error message:", fetchError.message);
        throw fetchError;
      }
    } else {
      // For text-only messages, use chat messageType and chat webhook
      console.log("Sending text-only message using JSON to chat webhook...");
      
      const data = {
        text: message,
        category: persona,
        persona,
        files: [],
        messageType: 'chat', // Set to "chat" for text-only messages
        sessionId: sessionId || null,
        userId: userId || null,
        auth: basicAuthToken // Include auth in the body instead of headers
      };
      
      console.log("Request payload:", JSON.stringify(data, null, 2));
      console.log("Making JSON POST request with messageType 'chat'...");
      
      try {
        response = await fetch(webhookUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(data)
        });
        console.log("JSON POST request completed successfully");
      } catch (fetchError) {
        console.error("JSON fetch failed:", fetchError);
        console.error("Error name:", fetchError.name);
        console.error("Error message:", fetchError.message);
        throw fetchError;
      }
    }
    
    console.log("Response received!");
    console.log("Response status:", response.status);
    console.log("Response ok:", response.ok);
    console.log("Response headers:", Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      console.error("HTTP error! status:", response.status);
      const errorText = await response.text();
      console.error("Error response body:", errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    const responseText = await response.text();
    console.log("Raw response text:", responseText);
    
    let result;
    try {
      result = JSON.parse(responseText);
      console.log("Parsed response:", result);
      
      // Handle array response format: [{"output":"message"}]
      if (Array.isArray(result) && result.length > 0) {
        console.log("Response is an array, extracting first element");
        result = result[0];
        console.log("Extracted object:", result);
      }
      
      console.log("Response keys:", Object.keys(result));
      console.log("Output field value:", result.output);
      console.log("Response field value:", result.response);
      console.log("Message field value:", result.message);
    } catch (parseError) {
      console.error("Failed to parse response as JSON:", parseError);
      console.log("Response was not valid JSON, treating as plain text");
      result = { response: responseText };
    }
    
    const finalMessage = result.output || result.response || result.message || responseText || "Message sent successfully";
    console.log("Final message to display:", finalMessage);
    console.log("Final message type:", typeof finalMessage);
    console.log("Final message length:", finalMessage?.length);
    
    const returnValue = { message: finalMessage };
    console.log("Returning from sendMessage:", returnValue);
    
    return returnValue;
  } catch (error) {
    console.error("Error sending message:", error);
    console.error("Error type:", typeof error);
    console.error("Error name:", error instanceof Error ? error.name : 'Unknown');
    console.error("Error message:", error instanceof Error ? error.message : String(error));
    console.error("Error stack:", error instanceof Error ? error.stack : 'No stack trace');
    
    // Check if it's a specific type of network error
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      console.error("This is a 'Failed to fetch' error - likely CORS or network connectivity issue");
      console.error("Possible causes:");
      console.error("1. CORS policy blocking the request");
      console.error("2. Network connectivity issue");
      console.error("3. Server is down or unreachable");
      console.error("4. SSL certificate issues");
    }
    
    throw error;
  }
};

// Function for sending form data with authentication
export const sendFormDataWithAuth = async (url: string, formData: FormData): Promise<Response> => {
  // Allow both voice and file webhook URLs
  const allowedUrls = [WEBHOOK_URLS.voice, WEBHOOK_URLS.file];
  
  console.log("===== API UTILS =====");
  console.log("sendFormDataWithAuth called with URL:", url);
  
  // Allow webhook calls only to authorized URLs
  if (allowedUrls.includes(url)) {
    console.log("Webhook URL validated ✓");
    
    // Log the form data contents for debugging
    console.log("Form data entries:");
    for (const pair of formData.entries()) {
      if (pair[0] === 'data') {
        console.log(`${pair[0]}: [Blob/File data], size: ${(pair[1] as Blob).size} bytes`);
      } else {
        console.log(`${pair[0]}: ${pair[1]}`);
      }
    }
    
    try {
      console.log("Initiating POST fetch request to webhook URL...");
      
      // Create basic auth token and add it as form data instead of header
      const credentials = await getWebhookCredentials();
      const basicAuthToken = createBasicAuthToken(credentials.username, credentials.password);
      formData.append('auth', basicAuthToken);
      console.log("Adding Basic Auth as form data");
      
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });
      
      console.log("POST fetch completed with status:", response.status, response.statusText);
      
      // Log response headers for debugging
      console.log("Response headers:");
      response.headers.forEach((value, name) => {
        console.log(`${name}: ${value}`);
      });
      
      return response;
    } catch (error) {
      console.error("Network error during POST fetch operation:", error);
      throw error;
    }
  } else {
    // Disable any other webhook calls
    console.log("⚠️ Blocked webhook call to non-authorized URL:", url);
    console.log("Allowed URLs:", allowedUrls);
    console.log("Received URL:", url);
    return new Response(JSON.stringify({ success: false, message: "Webhook calls blocked for non-authorized services" }), {
      status: 403,
      headers: { 'Content-Type': 'application/json' }
    });
  }
};

// Function for sending JSON data to webhook
export const sendJsonWithAuth = async (url: string, data: any): Promise<Response> => {
  // Allow both chat and file webhook URLs
  const allowedUrls = [WEBHOOK_URLS.chat, WEBHOOK_URLS.file];
  
  console.log("===== API UTILS =====");
  console.log("sendJsonWithAuth called with URL:", url);
  
  if (allowedUrls.includes(url)) {
    console.log("Webhook URL validated ✓");
    
    // Add auth to the data instead of headers
    const credentials = await getWebhookCredentials();
    const basicAuthToken = createBasicAuthToken(credentials.username, credentials.password);
    
    const dataWithAuth = {
      ...data,
      auth: basicAuthToken
    };
    
    console.log("JSON data being sent:", JSON.stringify(dataWithAuth));
    
    try {
      console.log("Initiating JSON POST fetch request to webhook URL...");
      
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(dataWithAuth)
      });
      
      console.log("JSON POST fetch completed with status:", response.status, response.statusText);
      
      return response;
    } catch (error) {
      console.error("Network error during JSON POST fetch operation:", error);
      throw error;
    }
  } else {
    console.log("⚠️ Blocked webhook call to non-authorized URL:", url);
    return new Response(JSON.stringify({ success: false, message: "Webhook calls blocked for non-authorized services" }), {
      status: 403,
      headers: { 'Content-Type': 'application/json' }
    });
  }
};
